import './styles.css'
import { parseLook, randomLook, type Look } from '../../shared/cosmetics.ts'
import { cleanName } from '../../shared/moderation.ts'
import { posAt } from '../../shared/path.ts'
import { CLOSE, IDLE_MS, type Act, type Emote, type Pose, type ServerMsg } from '../../shared/protocol.ts'
import { BLOCKED, cellAt, DEFAULT_ROOM, grid, isRoomId, OPEN, ROOMS, type RoomId } from '../../shared/world.ts'
import { BRAND } from './brand.ts'
import { BUILD } from './config.ts'
import { fetchCensus, Net } from './game/net.ts'
import { Townsfolk } from './game/npcs.ts'
import { World, type Player } from './game/state.ts'
import { daylight, Scene } from './render/scene.ts'
import { load, save } from './ui/dom.ts'
import { EMOTE_KEYS, Hud, type Prefs } from './ui/hud.ts'
import { openMaker, randomName } from './ui/maker.ts'
import { pop } from './sfx.ts'

interface Profile { name: string; look: Look }

console.info(`${BRAND} build ${BUILD}`)

function playerKey(): string {
  const have = load<string>('blobland:key')
  if (have && /^[a-z0-9]{16,32}$/.test(have)) return have
  const b = crypto.getRandomValues(new Uint8Array(12))
  const k = [...b].map((x) => x.toString(36).padStart(2, '0').slice(-2)).join('')
  save('blobland:key', k)
  return k
}

function loadProfile(): Profile | undefined {
  const p = load<Profile>('blobland:profile')
  const name = cleanName(p?.name)
  const look = parseLook(p?.look)
  return name && look ? { name, look } : undefined
}

class App {
  world = new World()
  folk = new Townsfolk()
  private npcs: Player[] = []
  private place?: string
  private onSpot = false
  /** Places this browser has found, for the passport and fast travel. */
  private discovered = new Set<string>(load<string[]>('blobland:passport') ?? [])
  private discoverAt = 0
  scene: Scene
  net: Net
  hud: Hud
  key = playerKey()
  profile?: Profile
  prefs: Prefs = { names: false, time: 'auto', calm: matchMedia('(prefers-reduced-motion: reduce)').matches, sound: true, zoom: 1, ...load<Partial<Prefs>>('blobland:prefs') }
  room: RoomId = DEFAULT_ROOM
  private from?: RoomId
  private shard?: number
  private retries = 0
  private retryTimer = 0
  private lastInput = performance.now()
  private travelling = false
  private dozing = false
  private held = new Set<string>()
  private hover?: { x: number; y: number }
  private focus?: string
  private fade: HTMLElement
  private moved = false
  private hudAt = -1e9

  constructor() {
    const stage = document.getElementById('stage')!
    const canvas = document.createElement('canvas')
    canvas.setAttribute('aria-label', 'The town. Click a tile to walk there.')
    stage.append(canvas)
    this.scene = new Scene(canvas)
    this.scene.userZoom = this.prefs.zoom
    this.scene.timeMode = this.prefs.time
    this.fade = document.createElement('div')
    this.fade.className = 'fade'
    document.body.append(this.fade)
    this.net = new Net({ message: (m) => this.onMessage(m), open: () => this.onOpen(), close: (c, r, o) => this.onClose(c, r, o) })
    this.hud = new Hud({
      say: (text) => { this.touch(); this.net.send({ t: 'say', text }) },
      emote: (e) => this.emote(e),
      act: (a) => this.act(a),
      pose: (p) => this.pose(p),
      wardrobe: () => this.wardrobe(),
      travel: (r) => this.travel(r),
      place: (id) => this.goPlace(id),
      passport: () => ({ discovered: this.discovered }),
      zoom: (d) => this.zoomBy(d > 0 ? 1.15 : 1 / 1.15),
      prefs: (p) => this.setPrefs(p),
      hide: (id, hidden) => { if (hidden) this.world.hidden.add(id); else this.world.hidden.delete(id) },
      report: (id) => { this.net.send({ t: 'report', id }); this.world.hidden.add(id) },
      goto: (id) => this.goto(id),
      census: () => fetchCensus(),
    }, this.prefs)
    this.hud.bind(this.world)
    this.bindInput(canvas)
    const resize = () => this.scene.resize(window.innerWidth, window.innerHeight)
    window.addEventListener('resize', resize)
    resize()
    this.setRoom(this.room)
    requestAnimationFrame((t) => this.loop(t))
    setInterval(() => this.checkIdle(), 15_000)
  }

  start(): void {
    const hash = location.hash.slice(1)
    if (isRoomId(hash)) this.room = hash
    this.setRoom(this.room)
    this.profile = loadProfile()
    if (this.profile) { this.connect(); return }
    openMaker({ name: randomName(), look: randomLook() }, {
      first: true,
      onDone: (r) => { this.profile = r; save('blobland:profile', r); this.connect() },
    })
  }

  // ─── Connection ─────────────────────────────────────────────────────────

  private connect(): void {
    if (!this.profile) return
    clearTimeout(this.retryTimer)
    this.net.connect({ room: this.room, name: this.profile.name, look: this.profile.look, key: this.key, from: this.from, shard: this.shard, place: this.place })
  }

  private setRoom(id: RoomId): void {
    this.scene.setRoom(ROOMS[id])
    this.folk.setRoom(id)
  }

  private onOpen(): void {
    this.retries = 0
  }

  private onMessage(m: ServerMsg): void {
    const now = Date.now()
    if (m.t === 'welcome') {
      this.room = m.room
      this.from = undefined
      this.place = undefined
      this.shard = m.shard
      this.setRoom(m.room)
      this.world.apply(m, now)
      this.hud.resetLog(this.world)
      this.hud.veil(null)
      this.travelling = false
      this.moved = false
      this.fade.classList.remove('on')
      history.replaceState(null, '', m.room === DEFAULT_ROOM ? location.pathname : `#${m.room}`)
      return
    }
    if (m.t === 'notice') { this.hud.toast(m.text); return }
    const line = this.world.apply(m, now)
    if (line) {
      this.hud.chat(line, this.world)
      if (this.prefs.sound && line.id !== this.world.me) pop()
    }
  }

  private onClose(code: number, _reason: string, opened: boolean): void {
    if (this.travelling && opened) return
    const look = this.profile?.look
    if (code === CLOSE.replaced) {
      this.hud.veil({ title: `${BRAND} is open in another tab`, text: 'Your blob can only be in one place at a time.', look, button: { label: 'Play here', run: () => { this.hud.veil(null); this.connect() } } })
      return
    }
    if (code === CLOSE.full) {
      this.shard = (this.shard ?? 1) + 1
      this.retry(300)
      return
    }
    if (code === CLOSE.abuse) {
      this.hud.veil({ title: 'Whoa, slow down', text: 'That was a lot, very fast. Back in a few seconds.', look })
      this.retry(6000)
      return
    }
    if (this.dozing) return
    if (!opened) void this.reloadIfStale()
    this.retries++
    const wait = Math.min(15000, 600 * 2 ** Math.min(5, this.retries - 1))
    if (this.retries > 1 || !opened) this.hud.veil({ title: opened ? 'Reconnecting…' : `Can’t reach ${BRAND}`, text: opened ? 'The connection dropped. Hang tight.' : 'Trying again in a moment.', look })
    this.retry(wait)
  }

  /** A refused join may just mean a newer build is live: if so, load it. */
  private async reloadIfStale(): Promise<void> {
    if (BUILD === 'dev') return
    try {
      const v = (await (await fetch('/version.json', { cache: 'no-store' })).json()) as { revision?: string }
      if (v.revision && v.revision !== BUILD) location.reload()
    } catch { /* offline: the retry loop handles it */ }
  }

  private retry(ms: number): void {
    clearTimeout(this.retryTimer)
    this.retryTimer = window.setTimeout(() => this.connect(), ms)
  }

  private travel(to: RoomId, place?: string): void {
    if (this.travelling || to === this.room && this.net.connected) return
    this.travelling = true
    this.from = place ? undefined : this.room
    this.place = place
    this.room = to
    this.shard = undefined
    this.fade.classList.add('on')
    setTimeout(() => { this.world.players.clear(); this.world.me = ''; this.setRoom(to); this.connect() }, 220)
    // If the hop fails, the close handler takes over; never leave the screen white.
    setTimeout(() => { if (this.travelling) { this.travelling = false; this.fade.classList.remove('on') } }, 6000)
  }

  private touch(): void { this.lastInput = performance.now() }

  /** The map's "take me there": a hop within the town, or a trip back out to it. */
  private goPlace(id: string): void {
    this.touch()
    if (!this.discovered.has(id)) return
    if (this.room === DEFAULT_ROOM) {
      this.net.send({ t: 'warp', place: id })
      this.moved = false
    } else {
      this.travel(DEFAULT_ROOM, id)
    }
  }

  private checkIdle(): void {
    if (this.dozing || !this.net.connected || performance.now() - this.lastInput < IDLE_MS) return
    this.dozing = true
    this.net.close()
    this.hud.veil({
      title: 'You dozed off',
      text: 'We tucked your blob in after a while without moving. It keeps rooms honest about who is really here.',
      look: this.profile?.look,
      button: { label: 'Wake up', run: () => { this.dozing = false; this.touch(); this.hud.veil(null); this.connect() } },
    })
  }

  // ─── Intents ─────────────────────────────────────────────────────────────

  private walk(x: number, y: number): boolean {
    this.touch()
    if (!this.world.walkTo(x, y, Date.now())) return false
    this.moved = true
    this.net.send({ t: 'move', x, y })
    return true
  }

  private emote(e: Emote): void { this.touch(); this.net.send({ t: 'emote', e }) }

  private act(a: Act): void {
    this.touch()
    const me = this.world.self
    if (me) me.act = { a, at: Date.now() } // play our own at once
    this.net.send({ t: 'act', a })
  }

  private pose(p: Pose): void {
    this.touch()
    const me = this.world.self
    if (!me) return
    if (posAt(me.path, Date.now()).moving) return
    me.pose = me.pose === p ? 'stand' : p
    this.net.send({ t: 'pose', p: me.pose })
  }

  private goto(id: string): void {
    const p = this.world.players.get(id)
    if (!p) return
    const d = this.world.derive(p, Date.now())
    const g = grid(this.room)
    const tx = Math.floor(d.x), ty = Math.floor(d.y)
    for (const [dx, dy] of [[1, 0], [0, 1], [-1, 0], [0, -1], [1, 1], [-1, -1], [1, -1], [-1, 1]] as const) {
      if (cellAt(g, tx + dx, ty + dy) === OPEN && this.walk(tx + dx, ty + dy)) return
    }
  }

  private wardrobe(): void {
    if (!this.profile) return
    openMaker(this.profile, {
      first: false,
      onDone: (r) => {
        const renamed = r.name !== this.profile!.name
        this.profile = r
        save('blobland:profile', r)
        if (renamed) { this.hud.toast(`You’re ${r.name} now.`); this.connect(); return } // names travel in the join URL
        this.net.send({ t: 'look', look: r.look })
        const me = this.world.self
        if (me) me.look = r.look
      },
    })
  }

  private setPrefs(p: Partial<Prefs>): void {
    Object.assign(this.prefs, p)
    this.hud.prefs = this.prefs
    this.scene.timeMode = this.prefs.time
    save('blobland:prefs', this.prefs)
  }

  private zoomBy(k: number): void {
    this.setPrefs({ zoom: Math.max(0.6, Math.min(2.4, this.prefs.zoom * k)) })
    this.scene.userZoom = this.prefs.zoom
  }

  // ─── Input ───────────────────────────────────────────────────────────────

  private bindInput(canvas: HTMLCanvasElement): void {
    const pts = new Map<number, { x: number; y: number; x0: number; y0: number; t0: number }>()
    let pinch = 0
    canvas.addEventListener('pointerdown', (e) => {
      canvas.setPointerCapture(e.pointerId)
      pts.set(e.pointerId, { x: e.clientX, y: e.clientY, x0: e.clientX, y0: e.clientY, t0: performance.now() })
      if (pts.size === 2) { const [a, b] = [...pts.values()]; pinch = Math.hypot(a!.x - b!.x, a!.y - b!.y) }
    })
    canvas.addEventListener('pointermove', (e) => {
      const p = pts.get(e.pointerId)
      if (p) { p.x = e.clientX; p.y = e.clientY }
      if (pts.size === 2 && pinch) {
        const [a, b] = [...pts.values()]
        const d = Math.hypot(a!.x - b!.x, a!.y - b!.y)
        if (d > 0) { this.zoomBy(d / pinch); pinch = d }
        return
      }
      if (e.pointerType === 'mouse') {
        const t = this.scene.toTile(e.clientX, e.clientY)
        const tx = Math.floor(t.x), ty = Math.floor(t.y)
        this.hover = cellAt(grid(this.room), tx, ty) === BLOCKED ? undefined : { x: tx, y: ty }
        const who = this.scene.pick(e.clientX, e.clientY)
        this.focus = who && who !== this.world.me ? who : undefined
        canvas.style.cursor = this.focus ? 'pointer' : this.hover ? 'pointer' : 'default'
      }
    })
    const end = (e: PointerEvent) => {
      const p = pts.get(e.pointerId)
      pts.delete(e.pointerId)
      if (pts.size < 2) pinch = 0
      if (!p || e.type === 'pointercancel') return
      if (Math.hypot(p.x - p.x0, p.y - p.y0) > 10 || performance.now() - p.t0 > 600 || pts.size) return
      this.tap(e.clientX, e.clientY)
    }
    canvas.addEventListener('pointerup', end)
    canvas.addEventListener('pointercancel', end)
    canvas.addEventListener('pointerleave', () => { this.hover = undefined; this.focus = undefined })
    canvas.addEventListener('wheel', (e) => { e.preventDefault(); this.zoomBy(Math.exp(-e.deltaY * 0.0012)) }, { passive: false })

    window.addEventListener('keydown', (e) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || document.querySelector('.scrim')) return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      const k = e.key.toLowerCase()
      if (k === 'enter') { e.preventDefault(); this.hud.input.focus(); return }
      if (k === 'escape') { this.hud.closeAll(); return }
      if (['w', 'a', 's', 'd', 'arrowup', 'arrowdown', 'arrowleft', 'arrowright'].includes(k)) { e.preventDefault(); this.held.add(k); this.touch(); return }
      if (e.repeat) return
      if (EMOTE_KEYS[e.key]) this.emote(EMOTE_KEYS[e.key]!)
      else if (k === ' ') { e.preventDefault(); this.act('hop') }
      else if (k === 'x') this.pose('sit')
      else if (k === 'z') this.pose('nap')
      else if (k === 'm') void this.hud.openMap()
    })
    window.addEventListener('keyup', (e) => this.held.delete(e.key.toLowerCase()))
    window.addEventListener('blur', () => this.held.clear())
  }

  private tap(sx: number, sy: number): void {
    this.touch()
    const who = this.scene.pick(sx, sy)
    if (who?.startsWith('npc-')) { this.chatNpc(who); return }
    if (who && who !== this.world.me) { this.hud.openCard(who, sx, sy); return }
    const t = this.scene.toTile(sx, sy)
    this.walk(Math.floor(t.x), Math.floor(t.y))
  }

  /** Held direction keys walk one tile at a time, chaining steps before the last one ends. */
  private steer(now: number): void {
    if (!this.held.size) return
    const me = this.world.self
    if (!me) return
    const has = (...ks: string[]) => ks.some((k) => this.held.has(k))
    // Screen directions in tile space: up = (-1,-1), right = (+1,-1).
    let sx = 0, sy = 0
    if (has('w', 'arrowup')) sy -= 1
    if (has('s', 'arrowdown')) sy += 1
    if (has('a', 'arrowleft')) sx -= 1
    if (has('d', 'arrowright')) sx += 1
    if (!sx && !sy) return
    const dx = Math.sign(sx + sy), dy = Math.sign(sy - sx)
    if (!dx && !dy) return
    // Step from the waypoint we are heading to (or standing on). A long click-walk is
    // redirected at once; a single step is only extended as it is about to finish.
    const at = posAt(me.path, now)
    const pts = me.path.pts
    const last = pts.length / 2 - 1
    const ni = at.moving ? at.next : last
    const nx = pts[ni * 2]!, ny = pts[ni * 2 + 1]!
    if (at.moving && ni === last && Math.hypot(nx - at.x, ny - at.y) > 0.3) return
    const g = grid(this.room)
    const bx = Math.floor(nx), by = Math.floor(ny)
    for (const [cx, cy] of [[dx, dy], [dx, 0], [0, dy]] as const) {
      if (!cx && !cy) continue
      const tx = bx + cx, ty = by + cy
      if (cellAt(g, tx, ty) === BLOCKED) continue
      if (this.walk(tx, ty)) return
    }
  }

  /** Stamp the passport for any place we are standing in for the first time. */
  private discover(x: number, y: number): void {
    const places = ROOMS[DEFAULT_ROOM].places
    for (const p of places) {
      if (this.discovered.has(p.id) || Math.hypot(p.x - x, p.y - y) > (p.r ?? 6)) continue
      this.discovered.add(p.id)
      save('blobland:passport', [...this.discovered])
      this.hud.toast(`📍 You found ${p.name}! Passport ${this.discovered.size}/${places.length}`, 4200)
      if (this.prefs.sound) pop()
    }
  }

  /** Click a townsperson and they say one of their lines. */
  private chatNpc(id: string): void {
    const npc = this.folk.npc(id)
    const p = this.npcs.find((q) => q.id === id)
    if (!npc || !p) return
    const now = Date.now()
    const line = npc.lines[Math.floor(Math.random() * npc.lines.length)]!
    p.bubble = { text: line, at: now, until: now + 4500 + line.length * 50 }
    p.emote = { e: 'happy', until: now + 3000 }
  }

  // ─── Frame ───────────────────────────────────────────────────────────────

  private loop(t: number): void {
    const now = Date.now()
    this.steer(now)
    // Walking onto an exit takes you through it.
    const me = this.world.self
    const meD = me ? this.world.derive(me, now) : undefined
    if (meD && this.moved && !this.travelling && meD.state !== 'walk') {
      const g = grid(this.room)
      const tx = Math.floor(meD.x), ty = Math.floor(meD.y)
      const portal = g.portals.get(ty * g.w + tx)
      if (portal) this.travel(portal.to)
      // Standing on a special spot (the mirror, a tram stop) does its thing once per visit.
      const spot = ROOMS[this.room].spots.find((s) => s.x === tx && s.y === ty)
      if (spot && !this.onSpot) { if (spot.action === 'wardrobe') this.wardrobe(); else void this.hud.openMap({ tram: true }) }
      this.onSpot = !!spot
    }
    if (meD && this.room === DEFAULT_ROOM && now - this.discoverAt > 400) { this.discoverAt = now; this.discover(meD.x, meD.y) }
    // Townsfolk: arriving somewhere, they may emote or say something if you're nearby.
    this.npcs = this.folk.update(now, this.world.offset, (p, npc, stop) => {
      if (stop.emote) p.emote = { e: stop.emote, until: now + 5000 }
      if (stop.say === undefined || !meD) return
      const d = this.world.derive(p, now)
      if (Math.hypot(d.x - meD.x, d.y - meD.y) > 9) return
      const line = stop.say >= 0 ? npc.lines[stop.say]! : npc.lines[Math.floor(Math.random() * npc.lines.length)]!
      p.bubble = { text: line, at: now, until: now + 4500 + line.length * 50 }
    })
    this.scene.frame(this.world, now, { hover: this.hover, focus: this.focus, names: this.prefs.names, calm: this.prefs.calm, npcs: this.npcs })
    if (t - this.hudAt > 400) { this.hudAt = t; this.hud.update(this.world, this.profile?.look, daylight(this.prefs.time)) }
    requestAnimationFrame((tt) => this.loop(tt))
  }
}

const app = new App()
app.start()
// Dev-only handle for profiling and debugging from the console.
if (import.meta.env.DEV) Object.assign(window, { blobland: app })
